/**
 * Cart trending product carousel: free native scrolling with clone-free
 * infinite looping via DOM node rotation when [loop] is present and
 * there are at least 4 slides.
 *
 * Desktop (fine pointer): grab-and-drag scrolling. Mobile: native touch
 * swipe. No forced snap-to-card — scrolling stops wherever the user
 * releases, same as native momentum scroll.
 *
 * Scrollability is signalled by the prev/next arrow buttons in the block
 * header (`[data-cart-trending-direction]`), which also step the carousel
 * one viewport-worth of slides at a time.
 */
class CartTrendingCarousel extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._onScroll = this._onScroll.bind(this);
    this._onScrollEnd = this._onScrollEnd.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
    this._onMouseClick = this._onMouseClick.bind(this);
    this._onDragStart = this._onDragStart.bind(this);
    this._onFinePointerChange = this._onFinePointerChange.bind(this);
    this._onNavClick = this._onNavClick.bind(this);

    this._isCompensating = false;
    this._threshold = 0;
    this._seeded = false;
    this._isDragging = false;
    this._dragMoved = false;
    this._dragPosition = null;
    this._dragTarget = null;
    this._awaitingScrollEnd = false;
    this._scrollEndTimeout = null;

    this.addEventListener('scroll', this._onScroll, {passive: true});
    // Images and links inside the cards are natively draggable; the browser's
    // drag-and-drop steals the pointer and kills our mousemove stream, so the
    // carousel would only drag from empty space. Suppress it outright.
    this.addEventListener('dragstart', this._onDragStart);

    this._resizeObserver = new ResizeObserver(() => {
      if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = requestAnimationFrame(this._onResize);
    });
    this._resizeObserver.observe(this);

    this._finePointerQuery = window.matchMedia('screen and (pointer: fine)');
    this._finePointerQuery.addEventListener('change', this._onFinePointerChange);
    this._syncDragListeners(this._finePointerQuery.matches);

    this._bindNav();

    this._measure();
    this._seed();
    this._updateEdges();
  }

  disconnectedCallback() {
    this.removeEventListener('scroll', this._onScroll);
    this.removeEventListener('scrollend', this._onScrollEnd);
    this.removeEventListener('mousedown', this._onMouseDown);
    this.removeEventListener('mousemove', this._onMouseMove);
    this.removeEventListener('click', this._onMouseClick, true);
    this.removeEventListener('dragstart', this._onDragStart);
    document.removeEventListener('mouseup', this._onMouseUp);

    if (this._finePointerQuery) {
      this._finePointerQuery.removeEventListener('change', this._onFinePointerChange);
      this._finePointerQuery = null;
    }

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this._scrollEndTimeout) clearTimeout(this._scrollEndTimeout);
    if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);

    if (this._navButtons) {
      this._navButtons.forEach((button) => button.removeEventListener('click', this._onNavClick));
      this._navButtons = null;
    }
    this._nav = null;

    this.classList.remove('is-dragging');
    this._initialized = false;
  }

  get slides() {
    return Array.from(this.children).filter((el) => el.nodeType === 1);
  }

  get canLoop() {
    return this.hasAttribute('loop') && this.slides.length >= 4;
  }

  _onDragStart(event) {
    event.preventDefault();
  }

  _onFinePointerChange(event) {
    this._syncDragListeners(event.matches);
  }

  _syncDragListeners(enabled) {
    this.removeEventListener('mousedown', this._onMouseDown);
    if (enabled) {
      this.addEventListener('mousedown', this._onMouseDown);
      this.classList.add('is-draggable');
    } else {
      this.classList.remove('is-draggable');
    }
  }

  /**
   * The arrows live in the block header, outside this element, so they can sit
   * next to the heading instead of covering the cards.
   */
  _bindNav() {
    const container = this.closest('.cart-trending-products');
    this._nav = container ? container.querySelector('[data-cart-trending-nav]') : null;
    this._navButtons = this._nav
      ? Array.from(this._nav.querySelectorAll('[data-cart-trending-direction]'))
      : [];

    this._navButtons.forEach((button) => button.addEventListener('click', this._onNavClick));
  }

  _onNavClick(event) {
    const direction = event.currentTarget.getAttribute('data-cart-trending-direction');
    const sign = direction === 'prev' ? -1 : 1;

    this.scrollBy({left: sign * this._scrollStep(), behavior: 'smooth'});
  }

  /** One viewport-worth of slides, rounded down to whole cards. */
  _scrollStep() {
    if (!this._slideStep) return this.clientWidth;

    const slides = Math.max(1, Math.floor((this.clientWidth * 0.9) / this._slideStep));
    return slides * this._slideStep;
  }

  _measure() {
    const slides = this.slides;
    if (slides.length === 0) return;

    const first = slides[0];
    const slideWidth = first.getBoundingClientRect().width;
    if (slideWidth <= 0) {
      this._slideStep = 0;
      return;
    }

    const styles = getComputedStyle(this);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;

    this._slideStep = slideWidth + gap;
    // Rotate before a full slide enters/leaves the viewport edge.
    this._threshold = Math.max(this._slideStep * 0.75, 1);
  }

  _onResize() {
    this._measure();
    if (!this._seeded) this._seed();
    this._updateEdges();
  }

  /**
   * Reflect scrollability in the arrow buttons: hidden entirely when the track
   * fits, otherwise enabled per available direction. In loop mode there is
   * always more content either way once the track overflows, so both stay on.
   */
  _updateEdges() {
    if (!this._nav || this._navButtons.length === 0) return;

    const maxScroll = this.scrollWidth - this.clientWidth;
    const scrollable = maxScroll > 1;

    this._nav.hidden = !scrollable;

    const loops = this.canLoop;

    this._navButtons.forEach((button) => {
      const isPrev = button.getAttribute('data-cart-trending-direction') === 'prev';
      let enabled = false;

      if (scrollable) {
        if (loops) {
          enabled = true;
        } else {
          enabled = isPrev ? this.scrollLeft > 1 : this.scrollLeft < maxScroll - 1;
        }
      }

      button.disabled = !enabled;
    });
  }

  /**
   * Place one slide before the viewport so the user can scroll either direction
   * immediately, without waiting for a forward scroll first.
   */
  _seed() {
    if (this._seeded || !this.canLoop || !this._slideStep) return;

    const last = this.lastElementChild;
    if (!last) return;

    this._isCompensating = true;
    this.insertBefore(last, this.firstElementChild);
    this.scrollLeft = this._slideStep;
    this._isCompensating = false;
    this._seeded = true;
  }

  /**
   * Native touch/wheel scrolling drives scrollLeft outside our control, with
   * its own momentum/deceleration animation. Rotating slides and writing
   * scrollLeft mid-gesture (to compensate) fights that animation — on iOS in
   * particular, an external scrollLeft write cancels an in-flight momentum
   * scroll outright, cutting a forward swipe short right as it nears the
   * loop seam and before the newly-rotated slide is reachable. Deferring the
   * rotation to scrollend (idle scrollLeft, nothing to interrupt) avoids
   * that entirely. Desktop drag calls _balance() directly instead, since
   * that scrollLeft is already fully under our control.
   */
  _onScroll() {
    this._updateEdges();

    if (this._isDragging || this._isCompensating) return;

    if (this._scrollEndTimeout) clearTimeout(this._scrollEndTimeout);

    if ('onscrollend' in window) {
      if (!this._awaitingScrollEnd) {
        this._awaitingScrollEnd = true;
        this.addEventListener('scrollend', this._onScrollEnd, {once: true});
      }
    } else {
      this._scrollEndTimeout = setTimeout(this._onScrollEnd, 120);
    }
  }

  _onScrollEnd() {
    this._awaitingScrollEnd = false;
    this._scrollEndTimeout = null;
    if (this.canLoop) this._balance();
  }

  _balance() {
    if (!this.canLoop || this._isCompensating || this._isDragging) return;

    const slides = this.slides;
    if (slides.length < 4 || !this._slideStep) return;

    const maxScroll = this.scrollWidth - this.clientWidth;
    if (maxScroll <= 0) return;

    // Near the end: move the first slide to the end and compensate left.
    while (this.scrollLeft > maxScroll - this._threshold) {
      const first = this.firstElementChild;
      if (!first) break;

      this._isCompensating = true;
      this.appendChild(first);
      this.scrollLeft -= this._slideStep;
      this._isCompensating = false;

      const nextMax = this.scrollWidth - this.clientWidth;
      if (this.scrollLeft <= nextMax - this._threshold) break;
    }

    // Near the start: move the last slide to the front and compensate right.
    while (this.scrollLeft < this._threshold) {
      const last = this.lastElementChild;
      if (!last) break;

      this._isCompensating = true;
      this.insertBefore(last, this.firstElementChild);
      this.scrollLeft += this._slideStep;
      this._isCompensating = false;

      if (this.scrollLeft >= this._threshold) break;
    }
  }

  _onMouseDown(event) {
    if (event.button !== 0) return;
    // Let form controls and buttons work without starting a drag.
    if (event.target.closest('button, input, label, select, textarea')) return;

    // Blocks the native image/link drag that would otherwise hijack the
    // pointer. Click still fires, so links and Quick Buy keep working; focus is
    // restored manually on mouseup below since preventDefault suppresses it.
    event.preventDefault();

    this._isDragging = true;
    this._dragMoved = false;
    this._dragTarget = event.target;
    this._dragPosition = {
      left: this.scrollLeft,
      x: event.clientX,
    };

    this.classList.add('is-dragging');

    this.addEventListener('mousemove', this._onMouseMove);
    this.addEventListener('click', this._onMouseClick, true);
    document.addEventListener('mouseup', this._onMouseUp);
  }

  _onMouseMove(event) {
    if (!this._isDragging || !this._dragPosition) return;

    const dx = event.clientX - this._dragPosition.x;
    if (Math.abs(dx) > 3) this._dragMoved = true;

    event.preventDefault();
    this.scrollLeft = this._dragPosition.left - dx;

    if (this.canLoop) this._balance();
    this._updateEdges();
  }

  _onMouseUp() {
    if (!this._isDragging) return;

    this._isDragging = false;
    this.classList.remove('is-dragging');
    this.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);

    if (!this._dragMoved) {
      // A click, not a drag: hand focus to the control the user pressed, which
      // the mousedown preventDefault would otherwise have skipped.
      const focusable = this._dragTarget && this._dragTarget.closest('a[href], button, [tabindex]');
      if (focusable) focusable.focus({preventScroll: true});
    }

    this._dragTarget = null;
  }

  _onMouseClick(event) {
    // Suppress click-through after a real drag (links / add buttons).
    if (this._dragMoved) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.removeEventListener('click', this._onMouseClick, true);
  }
}

if (!customElements.get('cart-trending-carousel')) {
  customElements.define('cart-trending-carousel', CartTrendingCarousel);
}
